import { useState, useEffect } from 'react';
import { X, Send, Copy, CheckCircle2 } from 'lucide-react';

// Modal pro pozvání zaměstnance do onboardingu. Admin zadá základní info
// (jméno, kontaktní e-mail, pozice, oddělení, kariéra, …). Backend založí kostru
// účtu (stav 'novy') a pošle uvítací PIN na kontaktní e-mail.
// POST /api/onboarding/pozvanky → { ok, zamestnanec_id, uzivatel_id, pin_platnost_do, pin? }

export default function PozvatModal({ isDark, onClose, onCreated }) {
    const [pozice, setPozice] = useState([]);
    const [oddeleni, setOddeleni] = useState([]);
    const [kariery, setKariery] = useState([]);
    const [typyUvazku, setTypyUvazku] = useState([]);
    const [zamestnanci, setZamestnanci] = useState([]);

    const [form, setForm] = useState({
        jmeno: '', prijmeni: '', kontaktni_email: '',
        pozice_id: '', nadrizeny_id: '', typ_uvazku_id: '', datum_nastupu: '',
    });
    const [oddeleniIds, setOddeleniIds] = useState([]);
    const [karieraIds, setKarieraIds] = useState([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [hotovo, setHotovo] = useState(null); // { pin, pin_platnost_do }
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetch('/api/ciselniky/pozice').then(r => r.json()).then(setPozice).catch(() => {});
        fetch('/api/ciselniky/oddeleni').then(r => r.json()).then(setOddeleni).catch(() => {});
        fetch('/api/ciselniky/kariery').then(r => r.json()).then(setKariery).catch(() => {});
        fetch('/api/ciselniky/typy-uvazku').then(r => r.json()).then(setTypyUvazku).catch(() => {});
        fetch('/api/zamestnanci').then(r => r.json()).then(setZamestnanci).catch(() => {});
    }, []);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const cardStyle = {
        background: isDark ? '#1a1a26' : 'var(--surface-raised)',
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
    };
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)',
        borderRadius: 6, padding: '7px 12px', outline: 'none', fontSize: 14, width: '100%',
        colorScheme: isDark ? 'dark' : 'light',
    };
    const optionStyle = { background: isDark ? '#1e1e2e' : '#fff', color: isDark ? '#e2e8f0' : '#1e293b' };
    const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider mb-1';

    async function odeslat(e) {
        e.preventDefault();
        setError('');
        if (!form.jmeno || !form.prijmeni || !form.kontaktni_email) { setError('Vyplň jméno, příjmení a kontaktní e-mail.'); return; }
        if (karieraIds.length === 0) { setError('Vyber alespoň jednu kariéru (řídí povinná školení a certifikace).'); return; }
        setBusy(true);
        try {
            const res = await fetch('/api/onboarding/pozvanky', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    pozice_id: form.pozice_id || null,
                    nadrizeny_id: form.nadrizeny_id || null,
                    typ_uvazku_id: form.typ_uvazku_id || null,
                    datum_nastupu: form.datum_nastupu || null,
                    oddeleni_ids: oddeleniIds,
                    kariera_ids: karieraIds,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setHotovo({ pin: data.pin, pin_platnost_do: data.pin_platnost_do });
                onCreated?.();
            } else {
                const first = data.errors ? Object.values(data.errors)[0]?.[0] : null;
                setError(first || data.message || data.error || `Chyba ${res.status}`);
            }
        } catch (err) {
            setError('Nepodařilo se odeslat pozvánku.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
            style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="w-full max-w-lg mt-10" style={cardStyle} onClick={e => e.stopPropagation()}>
                <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${borderColor}` }}>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Pozvat zaměstnance do onboardingu</h3>
                    <button type="button" onClick={onClose} className="p-1 rounded-md" style={{ color: mutedColor }}><X size={18} /></button>
                </div>

                {hotovo ? (
                    <div className="p-5 flex flex-col items-center gap-3 text-center">
                        <CheckCircle2 size={40} className="text-emerald-500" />
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Pozvánka odeslána na {form.kontaktni_email}</p>
                        {hotovo.pin ? (
                            <>
                                <p className="text-xs" style={{ color: mutedColor }}>Uvítací PIN (dev — v produkci jen e-mailem):</p>
                                <button onClick={() => { navigator.clipboard?.writeText(hotovo.pin); setCopied(true); }}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-xl tracking-widest"
                                    style={{ ...inputStyle, width: 'auto' }}>
                                    {hotovo.pin} {copied ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Copy size={16} />}
                                </button>
                            </>
                        ) : (
                            <p className="text-xs" style={{ color: mutedColor }}>PIN byl odeslán na kontaktní e-mail.</p>
                        )}
                        <button onClick={onClose} className="mt-2 bg-amber-500 hover:bg-amber-600 text-gray-950 font-semibold px-4 py-2 rounded-lg text-sm">Hotovo</button>
                    </div>
                ) : (
                    <form onSubmit={odeslat} className="p-5 flex flex-col gap-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={labelCls} style={{ color: mutedColor }}>Jméno *</label>
                                <input style={inputStyle} value={form.jmeno} onChange={e => set('jmeno', e.target.value)} /></div>
                            <div><label className={labelCls} style={{ color: mutedColor }}>Příjmení *</label>
                                <input style={inputStyle} value={form.prijmeni} onChange={e => set('prijmeni', e.target.value)} /></div>
                        </div>
                        <div><label className={labelCls} style={{ color: mutedColor }}>Kontaktní e-mail *</label>
                            <input type="email" style={inputStyle} value={form.kontaktni_email} onChange={e => set('kontaktni_email', e.target.value)} placeholder="osobni@email.cz" /></div>

                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={labelCls} style={{ color: mutedColor }}>Pozice</label>
                                <select style={inputStyle} value={form.pozice_id} onChange={e => set('pozice_id', e.target.value)}>
                                    <option value="" style={optionStyle}>—</option>
                                    {pozice.map(p => <option key={p.id} value={p.id} style={optionStyle}>{p.nazev}</option>)}
                                </select></div>
                            <div><label className={labelCls} style={{ color: mutedColor }}>Nadřízený</label>
                                <select style={inputStyle} value={form.nadrizeny_id} onChange={e => set('nadrizeny_id', e.target.value)}>
                                    <option value="" style={optionStyle}>—</option>
                                    {zamestnanci.map(z => <option key={z.id} value={z.id} style={optionStyle}>{z.jmeno} {z.prijmeni}</option>)}
                                </select></div>
                        </div>

                        <div>
                            <label className={labelCls} style={{ color: mutedColor }}>Kariéra * <span className="normal-case font-normal">(řídí povinná školení / certifikace)</span></label>
                            <div className="flex flex-wrap gap-2">
                                {kariery.map(k => (
                                    <button type="button" key={k.id} onClick={() => toggle(karieraIds, setKarieraIds, k.id)}
                                        className="px-3 py-1.5 rounded-lg text-sm transition-colors"
                                        style={karieraIds.includes(k.id)
                                            ? { background: '#f59e0b', color: '#0a0a0a', fontWeight: 600 }
                                            : { ...inputStyle, width: 'auto' }}>
                                        {k.nazev}
                                    </button>
                                ))}
                                {kariery.length === 0 && <span className="text-xs" style={{ color: mutedColor }}>Žádné kariéry v číselníku.</span>}
                            </div>
                        </div>

                        <div>
                            <label className={labelCls} style={{ color: mutedColor }}>Oddělení <span className="normal-case font-normal">(řídí směrnice k podpisu)</span></label>
                            <div className="flex flex-wrap gap-2">
                                {oddeleni.map(o => (
                                    <button type="button" key={o.id} onClick={() => toggle(oddeleniIds, setOddeleniIds, o.id)}
                                        className="px-3 py-1.5 rounded-lg text-sm transition-colors"
                                        style={oddeleniIds.includes(o.id)
                                            ? { background: '#f59e0b', color: '#0a0a0a', fontWeight: 600 }
                                            : { ...inputStyle, width: 'auto' }}>
                                        {o.nazev}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={labelCls} style={{ color: mutedColor }}>Typ úvazku</label>
                                <select style={inputStyle} value={form.typ_uvazku_id} onChange={e => set('typ_uvazku_id', e.target.value)}>
                                    <option value="" style={optionStyle}>—</option>
                                    {typyUvazku.map(t => <option key={t.id} value={t.id} style={optionStyle}>{t.nazev}</option>)}
                                </select></div>
                            <div><label className={labelCls} style={{ color: mutedColor }}>Datum nástupu</label>
                                <input type="date" style={inputStyle} value={form.datum_nastupu} onChange={e => set('datum_nastupu', e.target.value)} /></div>
                        </div>

                        {error && <p className="text-sm text-red-500">{error}</p>}

                        <div className="flex justify-end gap-2 mt-1">
                            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm" style={{ color: mutedColor }}>Zrušit</button>
                            <button type="submit" disabled={busy}
                                className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-gray-950 font-semibold px-4 py-2 rounded-lg text-sm disabled:opacity-60">
                                <Send size={15} /> {busy ? 'Odesílám…' : 'Odeslat pozvánku'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
